/**
 * Copy text to the clipboard, reporting whether it actually worked.
 *
 * Why this exists: `navigator.clipboard.writeText` throws `NotAllowedError` on a
 * real HTTPS origin whenever the `clipboard-write` permission sits in the
 * `prompt` state — even inside a genuine user gesture. Verified against
 * https://trademagic.gg in Chromium. Call sites that only `console.log` on
 * success therefore look completely dead to the user: the copy may well have
 * succeeded via the fallback, but nothing on screen ever says so.
 *
 * The deprecated `document.execCommand('copy')` path still succeeds in that
 * case, so it stays as a fallback — but its boolean return is CHECKED here.
 * It returns `false` rather than throwing when it fails, so an unchecked call
 * silently reports success that never happened.
 *
 * Callers must branch on the returned boolean and show confirmation.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Permission denied / insecure context / unsupported — fall through.
  }

  // Fallback: a detached textarea + execCommand. Positioned off-screen rather
  // than hidden, because `display: none` and `visibility: hidden` elements
  // cannot be selected, which makes the copy a no-op.
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '-9999px';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);

    textArea.select();
    // iOS Safari ignores select() on a readonly textarea without an explicit range.
    textArea.setSelectionRange(0, text.length);

    const succeeded = document.execCommand('copy');
    document.body.removeChild(textArea);
    return succeeded;
  } catch {
    return false;
  }
};

export default copyToClipboard;
